import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';

const AmazonBrandRegistry = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic Package", price: "$199", features: ["Trademark Filing", "Brand Registry Setup", "Basic Brand Protection", "Email Support", "Standard Processing"] },
    { name: "Complete Package", price: "$399", popular: true, features: ["Everything in Basic", "Trademark Search", "A+ Content Setup", "Attorney Review", "Priority Support", "Brand Analytics Access"] },
    { name: "Premium Package", price: "$599", features: ["Everything in Complete", "Dedicated Account Manager", "24/7 Premium Support", "Multi-Brand Registration", "Sponsored Brands Setup", "Ongoing Brand Monitoring"] }
  ];

  const process = [
    { step: "01", title: "Trademark Registration", description: "File and register your trademark with USPTO - required for Amazon Brand Registry enrollment." },
    { step: "02", title: "Brand Registry Enrollment", description: "Complete Amazon Brand Registry application with all required documentation and verification." },
    { step: "03", title: "Brand Protection & Benefits", description: "Access enhanced brand protection tools, A+ Content, and sponsored brand advertising features." }
  ];

  const testimonials = [
    { name: "Maria Garcia", role: "Amazon Seller", text: "Seamless Brand Registry enrollment. My products are now fully protected!" },
    { name: "John Smith", role: "E-commerce Brand Owner", text: "Professional service from trademark to Brand Registry. Highly recommended!" },
    { name: "Alice Wong", role: "Amazon FBA Seller", text: "Quick enrollment with excellent support. Worth every penny!" },
    { name: "Carlos Rodriguez", role: "Online Retailer", text: "Expert guidance throughout the Brand Registry process. Great experience!" },
    { name: "Emma Thompson", role: "Amazon Vendor", text: "Fast and professional Brand Registry service. Five stars!" }
  ];

  const faqs = [
    { question: "What is Amazon Brand Registry?", answer: "Amazon Brand Registry is a program that provides brand owners with enhanced tools to protect their intellectual property and create better shopping experiences on Amazon." },
    { question: "Do I need a trademark for Brand Registry?", answer: "Yes, Amazon Brand Registry requires an active registered trademark with a government trademark office in an eligible country." },
    { question: "What are the benefits of Brand Registry?", answer: "Benefits include enhanced brand protection, access to A+ Content, sponsored brand ads, brand analytics, and powerful search and reporting tools." },
    { question: "How long does enrollment take?", answer: "After obtaining your trademark (8-12 months), Brand Registry enrollment typically takes 1-2 weeks once all requirements are met." },
    { question: "Can I enroll multiple brands?", answer: "Yes, you can enroll multiple brands in Amazon Brand Registry, each requiring its own registered trademark." }
  ];

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      <div className="pt-20 pb-16 bg-gradient-to-b from-[#131921] to-[#0F1419]">
        <div className="container mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors"><ArrowLeft className="w-4 h-4 mr-2"/>Back to Home</Link>
          <h1 className="text-5xl font-bold mb-6 text-[#FF9900]">Amazon Brand Registry</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Protect your brand on Amazon with Brand Registry enrollment. Complete package starting at $199.</p>
        </div>
      </div>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-purple-900 to-blue-900">
              <img src="https://images.unsplash.com/photo-1674027392838-d85710a5121d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxlY29tbWVyY2UlMjBzaG9wcGluZyUyMG9ubGluZSUyMHN0b3JlJTIwaWxsdXN0cmF0aW9uJTIwdmVjdG9yfGVufDB8fHx8MTc3NTA3MjUxMnww&ixlib=rb-4.1.0&q=85" alt="Amazon" className="w-full h-full object-cover"/>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About Amazon Brand Registry</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">Amazon Brand Registry provides powerful tools to protect your intellectual property, create better product listings with A+ Content, and access advanced search and reporting capabilities.</p>
              <p className="text-gray-300 mb-6 leading-relaxed">Our complete service handles trademark registration and Brand Registry enrollment, making it easy to unlock all Amazon seller benefits and protect your brand from counterfeiters.</p>
              <Button 
                onClick={() => setIsContactFormOpen(true)}
                className="bg-[#FF9900] hover:bg-orange-600 text-white"
              >Get Started Today</Button>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Our Simple 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, i) => (
              <div key={i} className="bg-[#0F1419] p-8 rounded-2xl border border-white/10 hover:border-[#FF9900]/50 transition-all">
                <div className="text-6xl font-bold text-[#FF9900] mb-4 opacity-20">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Brand Registry Packages</h2>
          <p className="text-center text-gray-300 mb-16">Complete solutions for Amazon sellers</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`relative bg-[#131921] p-8 rounded-2xl border-2 ${plan.popular ? 'border-[#FF9900]' : 'border-white/10'} hover:border-[#FF9900]/50 transition-all`}>
                {plan.popular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2"><span className="bg-[#FF9900] text-white px-4 py-1 rounded-full text-sm font-bold">MOST POPULAR</span></div>}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#FF9900] mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-start"><Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0"/><span className="text-gray-300">{f}</span></li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsContactFormOpen(true)}
                  className={`w-full ${plan.popular ? 'bg-[#FF9900] hover:bg-orange-600' : 'bg-white/10 hover:bg-white/20'} text-white`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Our Brand Registry Service?</h2>
              <div className="space-y-4">
                {["Complete Trademark & Registry", "Amazon Seller Experts", "A+ Content Setup", "Ongoing Support"].map((title, i) => (
                  <div key={i} className="flex items-start">
                    <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0"/>
                    <div><h3 className="text-xl font-bold mb-2">{title}</h3><p className="text-gray-300">End-to-end Brand Registry enrollment with expert guidance</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJ1c2luZXNzJTIwZ3Jvd3RoJTIwY2hhcnQlMjAzZHxlbnwwfHx8fDE3NzUwNzcwODl8MA&ixlib=rb-4.1.0&q=85" alt="Why Choose" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-r from-[#FF9900] to-orange-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Enroll in Brand Registry?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Join thousands of Amazon sellers protecting their brands with Brand Registry enrollment.</p>
          <Button 
            onClick={() => setIsContactFormOpen(true)}
            className="bg-white text-[#FF9900] hover:bg-gray-100 text-lg px-8 py-6">Start Your Brand Registry<ArrowRight className="ml-2 w-5 h-5"/></Button>
        </div>
      </section>
      <ServiceTestimonials 
        testimonials={testimonials}
        serviceName="Amazon Brand Registry"
      />
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#0F1419] p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-[#FF9900]">{faq.question}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      
      <ContactFormDialog 
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
};

export default AmazonBrandRegistry;
